/**
 * A aba Lançamentos com as importações em PDF e a Calculadora Petrella.
 *
 * Papel: monta a `AbaLancamentos` e liga os botões "Importar Folha (PDF)" e
 * "Importar Guia DAE (PDF)" (l. 438–439) às seleções de arquivo e às caixas de
 * conferência — o PDF é lido no navegador, e só o que a pessoa confirma vai à
 * API —, "Calculadora Petrella" (l. 440) à caixa dela, e o 🧾 da linha (l.
 * 3111) à página do recibo, numa aba nova. Pede a recorrência automática de
 * ajuda de custo quando a aba fica à vista, uma vez por dia.
 *
 * Como usar: é o painel que a página põe na aba Lançamentos, com o mês aberto.
 * Quem só tem `ver` não recebe os botões de importação, porque a
 * `AbaLancamentos` só os mostra a quem tem `editar`, e nem o mapa CPF→nome nem
 * a recorrência são pedidos.
 *
 * A leitura do PDF que falha avisa "Erro ao ler o PDF: " seguido da mensagem do
 * pdf.js, que fala do arquivo e nunca do conteúdo dele. O PDF sem funcionário
 * ou sem linha de guia — o escaneado, sem camada de texto, inclusive — avisa e
 * não abre caixa nenhuma.
 *
 * A folha em PDF só é lida com o mapa CPF→nome de `useNomesDaFolha` já chegado.
 * Sem ele, o nome de registro do PDF iria à API, que cria um favorecido novo
 * para cada nome que não casa; por isso a escolha do arquivo avisa "Aguarde os
 * nomes da folha carregarem" sem ler nada e, se a consulta falhou, pede o mapa
 * de novo para o próximo clique.
 */
import { acessoAlcanca } from '@concierge/shared';
import { useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { useAcesso } from '@/shared/hooks';
import { extrairTextoDoPdf } from '@/shared/lib/pdf';
import { abrirEmAbaNova, caminhoDoReciboDoLancamento } from '../documentos';
import { useCasaDoCaixa } from '../hooks/usar-financas';
import { useNomesDaFolha } from '../hooks/usar-financas-extras';
import { useRecorrenciaAoAbrir } from '../hooks/use-recorrencia';
import { type FolhaDoPdf, lerFolhaDoPdf } from '../importacao/folha-pdf';
import { type GuiaDoPdf, lerGuiaDae } from '../importacao/guia-dae';
import { AbaLancamentos } from './aba-lancamentos';
import { CalculadoraPetrella } from './calculadora-petrella';
import { ConferenciaDaFolha } from './conferencia-da-folha';
import { ConferenciaDaGuia } from './conferencia-da-guia';
import { EntradaDeArquivo } from './kit/entrada-de-arquivo';

interface PainelDeLancamentosProps {
  /** O mês aberto da página, em `AAAA-MM`. */
  mesAberto: string;
}

/** A caixa de conferência aberta, com o que foi lido do PDF. */
type ConferenciaAberta =
  { tipo: 'folha'; folha: FolhaDoPdf } | { tipo: 'guia'; guia: GuiaDoPdf };

/**
 * O texto de uma falha de leitura.
 *
 * @param erro - O que o pdf.js lançou.
 * @returns A mensagem do erro.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro);
}

/**
 * O painel da aba Lançamentos.
 *
 * @param mesAberto - O mês aberto da página.
 * @returns A aba com as seleções de arquivo e a caixa de conferência aberta.
 */
export function PainelDeLancamentos({ mesAberto }: PainelDeLancamentosProps) {
  const casa = useCasaDoCaixa();
  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'financas', 'editar');
  const notificar = useNotificacao();
  const nomes = useNomesDaFolha(casa.residenciaId, podeEditar);
  useRecorrenciaAoAbrir(casa.residenciaId, podeEditar);
  const [conferencia, setConferencia] = useState<ConferenciaAberta | null>(null);
  const [calculadoraAberta, setCalculadoraAberta] = useState(false);
  const arquivoDaFolha = useRef<HTMLInputElement>(null);
  const arquivoDaGuia = useRef<HTMLInputElement>(null);

  /**
   * Lê a folha em PDF e abre a conferência (l. 4279–4302).
   *
   * @param arquivo - O PDF escolhido.
   */
  async function handleFolha(arquivo: File) {
    const mapa = nomes.data;
    if (mapa === undefined) {
      if (nomes.isError) void nomes.refetch();
      notificar('Aguarde os nomes da folha carregarem', false);
      return;
    }
    let folha: FolhaDoPdf;
    try {
      folha = lerFolhaDoPdf(await extrairTextoDoPdf(arquivo), mapa);
    } catch (erro) {
      notificar(`Erro ao ler o PDF: ${mensagemDe(erro)}`, false);
      return;
    }
    if (folha.funcionarios.length === 0) {
      notificar('Não foi possível identificar funcionários no PDF. Confira o arquivo.', false);
      return;
    }
    setConferencia({ tipo: 'folha', folha });
  }

  /**
   * Lê a guia DAE em PDF e abre a conferência (l. 4445–4465).
   *
   * @param arquivo - O PDF escolhido.
   */
  async function handleGuia(arquivo: File) {
    let guia: GuiaDoPdf;
    try {
      guia = lerGuiaDae(await extrairTextoDoPdf(arquivo));
    } catch (erro) {
      notificar(`Erro ao ler o PDF: ${mensagemDe(erro)}`, false);
      return;
    }
    if (guia.itens.length === 0) {
      notificar(
        'Não foi possível identificar as linhas da guia no PDF. Confira o arquivo.',
        false,
      );
      return;
    }
    setConferencia({ tipo: 'guia', guia });
  }

  return (
    <>
      <AbaLancamentos
        mesAberto={mesAberto}
        onImportarFolhaPdf={() => arquivoDaFolha.current?.click()}
        onImportarGuiaDae={() => arquivoDaGuia.current?.click()}
        onAbrirCalculadora={() => setCalculadoraAberta(true)}
        onImprimirRecibo={(linha) => {
          if (!abrirEmAbaNova(caminhoDoReciboDoLancamento(linha.id))) {
            notificar(
              'O navegador bloqueou a nova janela. Permita pop-ups para gerar o recibo.',
              false,
            );
          }
        }}
      />
      {podeEditar && (
        <>
          <EntradaDeArquivo
            ref={arquivoDaFolha}
            aceita="application/pdf"
            rotulo="PDF da folha de pagamento"
            onArquivo={(arquivo) => void handleFolha(arquivo)}
          />
          <EntradaDeArquivo
            ref={arquivoDaGuia}
            aceita="application/pdf"
            rotulo="PDF da guia DAE"
            onArquivo={(arquivo) => void handleGuia(arquivo)}
          />
        </>
      )}
      {casa.residenciaId !== null && conferencia?.tipo === 'folha' && (
        <ConferenciaDaFolha
          folha={conferencia.folha}
          residenciaId={casa.residenciaId}
          onFechar={() => setConferencia(null)}
        />
      )}
      {casa.residenciaId !== null && calculadoraAberta && (
        <CalculadoraPetrella
          residenciaId={casa.residenciaId}
          onFechar={() => setCalculadoraAberta(false)}
        />
      )}
      {casa.residenciaId !== null && conferencia?.tipo === 'guia' && (
        <ConferenciaDaGuia
          guia={conferencia.guia}
          residenciaId={casa.residenciaId}
          onFechar={() => setConferencia(null)}
        />
      )}
    </>
  );
}
